import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ActiveOrderCard = ({ order, onUpdateStatus, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'var(--color-warning)';
      case 'confirmed':
        return 'var(--color-info)';
      case 'preparing':
        return 'var(--color-primary)';
      case 'ready':
        return 'var(--color-success)';
      case 'out_for_delivery':
        return 'var(--color-accent)';
      case 'delivered':
        return 'var(--color-muted-foreground)';
      case 'cancelled':
        return 'var(--color-destructive)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return 'Clock';
      case 'confirmed':
        return 'CheckCircle';
      case 'preparing':
        return 'ChefHat';
      case 'ready':
        return 'Package';
      case 'out_for_delivery':
        return 'Truck';
      case 'delivered':
        return 'CheckCircle2';
      case 'cancelled':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'out_for_delivery':
        return 'Out for Delivery';
      default:
        return status?.charAt(0)?.toUpperCase() + status?.slice(1);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date?.toLocaleDateString();
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6 transition-smooth hover:shadow-warm-md">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex items-start gap-4 flex-1">
          <Image
            src={order?.customerImage}
            alt={order?.customerImageAlt}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-heading font-semibold text-base md:text-lg text-foreground truncate">
                  {order?.customerName}
                </h4>
                <p className="text-sm text-muted-foreground">Order #{order?.orderId}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Placed: {formatDate(order?.timestamp)}
                </p>
              </div>
              <div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-shrink-0"
                style={{ backgroundColor: `${getStatusColor(order?.status)}15` }}
              >
                <Icon name={getStatusIcon(order?.status)} size={14} color={getStatusColor(order?.status)} />
                <span className="text-xs md:text-sm font-semibold capitalize" style={{ color: getStatusColor(order?.status) }}>
                  {getStatusLabel(order?.status)}
                </span>
              </div>
            </div>

            <div className="space-y-2 mb-3">
              {order?.items?.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Image
                    src={item?.image}
                    alt={item?.imageAlt}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex items-center justify-between flex-1 text-sm">
                    <span className="text-foreground">{item?.quantity}x {item?.name}</span>
                    <span className="font-semibold text-foreground data-text">${Number(item?.price_per_plate ?? item?.menu_item?.price_per_plate ?? item?.price ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            {order?.specialInstructions && (
              <div className="bg-muted rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <Icon name="MessageSquare" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-caption font-semibold text-foreground mb-1">Special Instructions:</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{order?.specialInstructions}</p>
                  </div>
                </div>
              </div>
            )}

            {order?.deliveryAddress && (
              <div className="bg-muted rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <Icon name="MapPin" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-caption font-semibold text-foreground mb-1">Delivery Address:</p>
                    <p className="text-sm text-muted-foreground">{order?.deliveryAddress}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Icon name="Clock" size={14} />
                <span>{formatTime(order?.timestamp)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Icon name={order?.deliveryType === 'delivery' ? 'Truck' : 'Store'} size={14} />
                <span className="capitalize">{order?.deliveryType}</span>
              </div>
              {order?.deliveryTime && (
                <div className="flex items-center gap-1.5">
                  <Icon name="Calendar" size={14} />
                  <span>{formatDate(order?.deliveryTime)}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Icon name="DollarSign" size={14} />
                <span className="font-semibold data-text">${Number(order?.totalAmount ?? 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex lg:flex-col gap-2 lg:w-32">
          <Button
            variant="default"
            size="sm"
            fullWidth
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onUpdateStatus(order?.orderId)}
          >
            Update
          </Button>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails(order?.orderId)}
          >
            Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActiveOrderCard;